/**
 * @description Hook utilizado para manipular os periciandos
 * @author Diego Sabino Amorim de Araújo
 * @file usePericiandoService.js
 * @license Copyright (c) 2022 SIGEP DIGETI ITEP/RN
 */

import actionTypes from 'utils/actionTypes'
import { useDispatch } from 'react-redux'
import { useFetch } from './useFetch'

export const usePericiandoService = () => {
  const fetch = useFetch()
  const dispatch = useDispatch()

  const listarPericiando = async (id, setPericiando, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/periciando/${id}`)
      setPericiando(response.data.periciando)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarPericiandoAsync = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/periciando/${id}`)
      return response.data.periciando
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarPericiandoDetalhadoAsync = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/periciando/detalhado/${id}`)
      return response.data.periciando
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarPericiandosDeCaso = async (idCaso, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`periciando/deCaso/${idCaso}`)
      dispatch({
        type: actionTypes.SET_PERICIANDOS_CASO,
        periciandos: response.data.periciandos,
      })
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarPericiandosDeCasoAsync = async (idCaso, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`periciando/deCaso/${idCaso}`)
      return response.data.periciandos
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const buscarRegistroCivil = async (cpf, setRegistroCivil, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/periciando/registroCivil/${cpf}`)
      setRegistroCivil(response.data.registroCivil)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const consultarCPFExistente = async (cpf, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/periciando/consultarCPFExistente/${cpf}`)
      return response.data.cpfExiste
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const cadastrarPericiando = async (dados, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.postOpen(`/periciando/`, dados)
      return response.data.periciando
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const editarPericiando = async (id, dados, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      await fetch.putOpen(`/periciando/${id}`, dados)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const excluirPericiando = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      await fetch.del(`/periciando/${id}`)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarRegistrosIdentificacaoPericiandoAsync = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/periciando/registrosIdentificacao/${id}`)
      return response.data.registrosIdentificacao
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarRegistroIdentificacaoAsync = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/periciando/registroIdentificacao/porId/${id}`)
      return response.data.registroIdentificacao
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarPericiandosFiltradosAsync = async (dados, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const filtros = {
        nome: dados.nome ? dados.nome : null,
        alcunha: dados.alcunha ? dados.alcunha : null,
        classificacao: dados.classificacao ? dados.classificacao : null,
        dataEntradaInicio: dados.dataEntradaChecked && dados.dataEntradaInicio ? dados.dataEntradaInicio : null,
        dataEntradaFinal: dados.dataEntradaChecked && dados.dataEntradaFinal ? dados.dataEntradaFinal : null,
        sexo: dados.sexo ? dados.sexo : null,
      }
      const response = await fetch.postOpenWithoutFeedback('periciando/filtrar/', filtros)
      return response.data.periciandos
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarPericiandosLikeStringAsync = async (string, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`periciando/filtrar/porstring/${string}`)
      return response.data.periciandos
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarPericiandosAllAsync = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/periciando/`)
      return response.data.periciandos
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const importarPericiandosNoCaso = async (idCaso, periciandosImportar, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      await fetch.postOpenWithoutFeedback(`/periciando/importarPericiandosNoCaso/${idCaso}`, periciandosImportar)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarCasosDePericiando = async (idPericiando, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/periciando/listarCasosDePericiando/${idPericiando}`)
      return response.data.casos
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  return {
    listarPericiandosDeCaso,
    listarPericiandosDeCasoAsync,
    listarPericiando,
    listarPericiandoAsync,
    cadastrarPericiando,
    editarPericiando,
    excluirPericiando,
    buscarRegistroCivil,
    listarPericiandoDetalhadoAsync,
    listarRegistrosIdentificacaoPericiandoAsync,
    listarRegistroIdentificacaoAsync,
    listarPericiandosFiltradosAsync,
    listarPericiandosLikeStringAsync,
    listarPericiandosAllAsync,
    consultarCPFExistente,
    importarPericiandosNoCaso,
    listarCasosDePericiando,
  }
}
